/**
 * Shared frontend logger implementation.
 *
 * Logs are written to the browser console and, when a transport is configured,
 * sent to a backend endpoint for ingestion into VictoriaLogs.
 */

import type { LogLevel, LogContext } from './types';
import { LOG_LEVELS } from './types';
import type { LogTransport } from './transport';
import { buildFingerprint, getErrorEventId } from './error-identity';
import { getRuntimeLogContext } from './runtime-context';
import {
  getBrowserLocale,
  getBrowserTimeZone,
  getPageViewId,
  getSessionTraceId,
} from './trace';

export interface ExternalErrorReport {
  error: Error | string;
  context?: Partial<LogContext>;
  eventId: string;
  fingerprint: string;
}

/** Configuration for the logger */
export interface LoggerConfig {
  /** App version for context */
  appVersion?: string;
  /** Release identifier for correlation/symbolication */
  release?: string;
  /** Commit hash for the current build */
  commitHash?: string;
  /** Build timestamp or build identifier */
  buildTime?: string;
  /** Environment name (production, staging, development, etc.) */
  environment?: string;
  /** Whether running in development mode */
  isDev?: boolean;
  /** Minimum log level (default: 'debug' in dev, 'info' in prod) */
  minLevel?: LogLevel;
  /** Optional transport for delivering logs to a backend */
  transport?: LogTransport;
  /** Optional external error reporter for vendor-backed incident capture */
  externalErrorReporter?: (report: ExternalErrorReport) => void;
}

export class Logger {
  private readonly config: Required<Omit<LoggerConfig, 'transport' | 'externalErrorReporter'>>;
  private readonly minLevel: LogLevel;
  private readonly transport: LogTransport | null;
  private readonly externalErrorReporter:
    | ((report: ExternalErrorReport) => void)
    | null;

  constructor(config: LoggerConfig) {
    const {
      appVersion = 'unknown',
      release = appVersion,
      commitHash = 'unknown',
      buildTime = 'unknown',
      environment,
      isDev = false,
      minLevel,
      transport,
      externalErrorReporter,
    } = config;

    this.config = {
      appVersion,
      release,
      commitHash,
      buildTime,
      environment: environment ?? (isDev ? 'development' : 'production'),
      isDev,
      minLevel: minLevel ?? (isDev ? 'debug' : 'info'),
    };
    this.minLevel = this.config.minLevel;
    this.transport = transport ?? null;
    this.externalErrorReporter = externalErrorReporter ?? null;
  }

  /**
   * Log a message with optional context
   */
  log(level: LogLevel, message: string, context?: LogContext): void {
    if (LOG_LEVELS.indexOf(level) < LOG_LEVELS.indexOf(this.minLevel)) {
      return;
    }

    const entry = this.createEntry(level, message, context);
    const consoleMethod =
      level === 'error' ? 'error' : level === 'warn' ? 'warn' : 'log';

    if (entry.context && Object.keys(entry.context).length > 0) {
      console[consoleMethod](`[${level.toUpperCase()}]`, message, entry.context);
    } else {
      console[consoleMethod](`[${level.toUpperCase()}]`, message);
    }

    // Deliver to transport (backend) if configured
    if (this.transport) {
      this.transport.send(entry);
    }
  }

  /** Flush any buffered log entries (call before app teardown) */
  async flush(): Promise<void> {
    if (this.transport) {
      await this.transport.flush();
    }
  }

  /**
   * Log a debug message
   */
  debug(message: string, context?: LogContext): void {
    this.log('debug', message, context);
  }

  /**
   * Log an info message
   */
  info(message: string, context?: LogContext): void {
    this.log('info', message, context);
  }

  /**
   * Log a warning message
   */
  warn(message: string, context?: LogContext): void {
    this.log('warn', message, context);
  }

  /**
   * Log an error message
   */
  error(message: string, context?: LogContext): void {
    this.log('error', message, context);
  }

  /**
   * Log an Error object with stack trace
   */
  logError(error: Error, context?: Partial<LogContext>): void {
    const errorEventId =
      typeof context?.error_event_id === 'string'
        ? context.error_event_id
        : getErrorEventId(error);

    const errorFingerprint =
      typeof context?.error_fingerprint === 'string'
        ? context.error_fingerprint
        : buildFingerprint([
            context?.component,
            context?.action,
            error.name,
            error.message,
            context?.source_file,
            context?.error_line,
            context?.error_col,
          ]);

    this.error(error.message, {
      error_name: error.name,
      error_stack: error.stack?.slice(0, 2000), // Limit stack trace size
      error_event_id: errorEventId,
      error_fingerprint: errorFingerprint,
      ...context,
    });
  }

  /**
   * Log an API error with request context
   */
  logApiError(
    message: string,
    endpoint: string,
    method: string,
    statusCode: number,
    errorCode?: string
  ): void {
    const codeTag = errorCode ? ` [${errorCode}]` : '';
    this.error(`API ${statusCode}${codeTag}: ${method} ${endpoint} — ${message}`, {
      url: endpoint,
      method,
      status_code: statusCode,
      error_code: errorCode,
      component: 'api-client',
      action: 'request_failed',
    });
  }

  /**
   * Log a WebSocket error
   */
  logWebSocketError(error: Error, wsContext: string): void {
    this.warn(`WebSocket error: ${error.message}`, {
      error_name: error.name,
      error_stack: error.stack?.slice(0, 2000),
      component: 'websocket-client',
      action: wsContext,
    });
  }

  reportExternalError(report: ExternalErrorReport): void {
    if (!this.externalErrorReporter) {
      return;
    }

    try {
      this.externalErrorReporter(report);
    } catch (error) {
      console.warn('[WARN]', 'External error reporter failed', error);
    }
  }

  /**
   * Create a log entry with auto-populated context
   */
  private createEntry(
    level: LogLevel,
    message: string,
    context?: LogContext
  ): { level: LogLevel; message: string; timestamp: string; context?: LogContext } {
    return {
      level,
      message,
      timestamp: new Date().toISOString(),
      context: {
        // Auto-populate environment context
        user_agent:
          typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        app_version: this.config.appVersion,
        release: this.config.release,
        commit_hash: this.config.commitHash,
        build_time: this.config.buildTime,
        environment: this.config.environment,
        session_id:
          typeof window !== 'undefined' ? getSessionTraceId() : undefined,
        page_view_id:
          typeof window !== 'undefined' ? getPageViewId() : undefined,
        client_locale: getBrowserLocale(),
        client_timezone: getBrowserTimeZone(),
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        ...getRuntimeLogContext(),
        // Spread provided context (can override auto-populated values)
        ...context,
      },
    };
  }
}

/**
 * Factory function to create a logger instance
 */
export function createLogger(config: LoggerConfig): Logger {
  return new Logger(config);
}
